/**
 * sidebarSectionState.ts — F 498 — THE FOLDED-SECTION STATE.
 *
 * The sidebar's three sections (Tasks, Databases, Pages) can be folded. The
 * operator asked for three things and this file is where two of them live:
 *
 *   1. the state is REMEMBERED across reloads, and
 *   2. it is remembered under ONE key, so one read/write pair owns it.
 *
 * WHY A SEPARATE FILE AND NOT A HOOK IN THE COMPONENT
 * ----------------------------------------------------
 * The state has to be read ON MOUNT, before the first paint, or the sidebar
 * flashes open and then snaps shut. A `useState` initialiser that reads
 * localStorage is synchronous, so that is possible — but only if the read is
 * in a place that can be unit-tested without mounting the whole sidebar.
 *
 * So the storage contract lives here, on its own, and the component is a
 * thin consumer of it. That is also what makes the FAULT TOLERANCE testable:
 * a corrupt localStorage value must not take the sidebar down, and that is a
 * property of THIS file, not of the component that happens to call it.
 *
 * THE KEY
 * -------
 * `freshcards.sidebar.sections` — the key the dispatch named. It holds a JSON
 * object of `section -> boolean`, where `true` means OPEN:
 *
 *     { "tasks": true, "databases": false, "pages": true }
 *
 * A section that is ABSENT from the object is OPEN. That is the default the
 * dispatch asked for ("Default: all open"), and it also means a key written by
 * an older build — or a key that is missing one section because the operator
 * never touched it — does not accidentally hide a section.
 *
 * WHY THE VALUES ARE BOOLEANS AND NOT "folded"/"open" STRINGS
 * -------------------------------------------------------------
 * A boolean has one falsey value. A string has several ways to be wrong
 * ("False", "false", "no", "0"), and a `Boolean("false")` is `true`. Parsing
 * defensively at the boundary is the whole job, and doing it once, here, is
 * what keeps every consumer honest.
 *
 * FAULT TOLERANCE
 * ---------------
 * localStorage can throw: Safari's private mode throws on write, a full quota
 * throws, and a cross-origin frame blocks access entirely. EVERY access here
 * is wrapped, and a failed write is a NO-OP that leaves the in-memory state
 * working. The fold must never be the reason the sidebar fails to render.
 *
 * The same applies to a CORRUPT value: `localStorage` holds a string, and
 * anything can be in it. A value that is not an object, or an object whose
 * values are not booleans, is treated as "no information" — every section
 * falls back to open. A corrupt key degrades to the default; it never hides a
 * section the operator cannot see.
 *
 * localStorage is also the RIGHT store here and not a fallback for a better
 * one. This is a pure UI preference, scoped to one browser, with no
 * cross-device requirement and no need for the FC Brain: Rule 1 §2.6 governs
 * DATA, and a fold state is not data. It is the operator's view of the app.
 */

import { useCallback, useEffect, useState } from 'react';

/** The key the dispatch named. */
export const SIDEBAR_SECTIONS_KEY = 'freshcards.sidebar.sections';

/** The three sections the dispatch named. */
export const SIDEBAR_SECTIONS = ['tasks', 'databases', 'pages'] as const;

/** One section's identity. */
export type SidebarSectionId = (typeof SIDEBAR_SECTIONS)[number];

/** The shape of the stored object: section -> open? */
export type SidebarSectionState = Record<SidebarSectionId, boolean>;

/** Every section open. The default. */
export const ALL_SECTIONS_OPEN: SidebarSectionState = {
  tasks: true,
  databases: true,
  pages: true,
};

/**
 * Is this a plain object? `null`, arrays and primitives are not, and treating
 * an array as a map would make `sections[0]` a section name.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Read the stored state.
 *
 * Returns every section OPEN when there is nothing usable to read. Every
 * failure mode — no localStorage, a throwing localStorage, a non-JSON string,
 * a JSON value that is not an object, a per-section value that is not a
 * boolean — lands on "all open", because a section that is wrongly visible
 * costs the operator one extra click, while a section that is wrongly hidden
 * costs them the ability to find it.
 */
export function readSidebarSectionState(): SidebarSectionState {
  const fallback: SidebarSectionState = { ...ALL_SECTIONS_OPEN };

  let raw: string | null = null;
  try {
    if (typeof localStorage === 'undefined' || localStorage === null) return fallback;
    raw = localStorage.getItem(SIDEBAR_SECTIONS_KEY);
  } catch {
    // Storage is unavailable or blocked. The fold still works for this
    // session; it just will not survive a reload.
    return fallback;
  }
  if (!raw) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // A corrupt value is not a reason to hide a section.
    return fallback;
  }
  if (!isPlainObject(parsed)) return fallback;

  const out: SidebarSectionState = { ...ALL_SECTIONS_OPEN };
  for (const id of SIDEBAR_SECTIONS) {
    const value = parsed[id];
    if (typeof value === 'boolean') out[id] = value;
  }
  return out;
}

/**
 * Write the stored state.
 *
 * Returns whether the write SUCCEEDED, so a caller can report it. A failed
 * write is not an error the operator needs to see — the fold works either
 * way — so the component does not surface it as a failure.
 */
export function writeSidebarSectionState(state: SidebarSectionState): boolean {
  try {
    if (typeof localStorage === 'undefined' || localStorage === null) return false;
    localStorage.setItem(SIDEBAR_SECTIONS_KEY, JSON.stringify(state));
    return true;
  } catch {
    // Quota, private mode, blocked storage. The in-memory state is still
    // correct; only persistence is lost.
    return false;
  }
}

/**
 * useSidebarSectionState — THE HOOK THE COMPONENT USES.
 *
 * The state is INITIALISED from storage, not loaded in an effect, so the very
 * first frame already shows the folded section. A `useEffect` read would
 * paint the sidebar open and then correct it a frame later — a visible flash
 * on every reload, which is the exact defect F 498 was dispatched to fix.
 *
 * The write is in an effect keyed on the state, so it runs after the fold has
 * been applied to the DOM and never blocks a click.
 */
export function useSidebarSectionState(): {
  state: SidebarSectionState;
  isOpen: (id: SidebarSectionId) => boolean;
  toggle: (id: SidebarSectionId) => void;
} {
  const [state, setState] = useState<SidebarSectionState>(readSidebarSectionState);

  // Persist on change. The dependency is the whole state object, so one
  // write covers any number of changed sections.
  useEffect(() => {
    writeSidebarSectionState(state);
  }, [state]);

  const isOpen = useCallback((id: SidebarSectionId) => state[id], [state]);

  const toggle = useCallback((id: SidebarSectionId) => {
    setState((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return { state, isOpen, toggle };
}
